import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { twMerge } from "tailwind-merge";
import { Toaster } from "react-hot-toast";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "components/ui/select";
import { ReactComponent as ArrowDown } from "assets/morning/icon/arrowDownNavy.svg";
import { ReactComponent as ReadingGlasses } from "assets/common/common/readingGlasses.svg";
import ConsoleTable, { DataListProps } from "../-components/ConsoleTable";
import Pagination from "components/Pagination";
import PageHeader from "../-components/PageHeader";
import Seo from "components/Seo";
import { makeIntComma } from "hooks/utils/util";
import { useGetConsoleAdminAccounts, AdminAccountListItem } from "hooks/console/useGetConsoleAdminAccounts";

const theadStyle = "text-[#222222] text-[16px] text-center";

export default function ConsoleAdminAccountList() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [searchForm, setSearchForm] = useState({
        searchtxt: "",
        page: Number(searchParams.get("page") ?? "1"),
        limit: "10",
    });
    const [searchInput, setSearchInput] = useState("");

    const { data, isLoading } = useGetConsoleAdminAccounts(searchForm);

    const handleSearch = () => {
        setSearchForm(prev => ({ ...prev, searchtxt: searchInput, page: 1 }));
        searchParams.set("page", "1");
        setSearchParams(searchParams);
    };

    return (
        <>
            <Seo type="console" pageTitle="관리자" />
            <div className="flex-1 flex flex-col overflow-hidden max-h-[100dvh]">
                <PageHeader category="관리자" title="계정 관리" />
                <div className="flex-1 bg-[#F7F6FB] flex overflow-hidden">
                    <div className="px-[40px] py-[28px] w-full overflow-y-auto">
                        <div className="bg-white rounded-[20px] py-[36px] px-[40px] w-full min-w-[900px]">
                            <form
                                onSubmit={e => {
                                    e.preventDefault();
                                    handleSearch();
                                }}
                            >
                                <div className="flex items-center gap-2 justify-end">
                                    <Select
                                        value={searchForm.limit}
                                        onValueChange={value => setSearchForm(prev => ({ ...prev, limit: value, page: 1 }))}
                                    >
                                        <SelectTrigger
                                            className={`relative shadow-none text-[#1B1C1E] text-[16px] flex gap-[10px] rounded-[5px] focus:ring-2 max-w-[150px] border border-[#E1E2E4] bg-white h-[50px]`}
                                        >
                                            <SelectValue placeholder="" />
                                            <ArrowDown className="[&_path]:fill-[#666666]" />
                                        </SelectTrigger>
                                        <SelectContent className="bg-white">
                                            <SelectItem value="20">20개</SelectItem>
                                            <SelectItem value="10">10개</SelectItem>
                                        </SelectContent>
                                    </Select>
                                    <div className="flex items-center border border-[#dddddd] rounded-[5px] bg-white overflow-hidden w-[320px] px-[16px]">
                                        <input
                                            type="text"
                                            value={searchInput}
                                            onChange={e => setSearchInput(e.target.value)}
                                            placeholder="아이디 또는 이름으로 검색"
                                            className={`text-[16px] placeholder:text-[#878A93] w-full focus:outline-none py-[12px]`}
                                        />
                                        <button type="submit" aria-label="검색">
                                            <ReadingGlasses />
                                        </button>
                                    </div>
                                </div>
                            </form>

                            <div className="mt-[8px] mb-[40px]">
                                <ConsoleTable
                                    thead={[
                                        {
                                            title: "NO",
                                            width: "15%",
                                            key: "index",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                            content: (row: DataListProps, rowIndex) => {
                                                const index = (data?.total_count ?? 0) - (searchForm.page - 1) * Number(searchForm.limit) - rowIndex;
                                                return <span>{makeIntComma(index) ?? 0}</span>;
                                            },
                                        },
                                        {
                                            title: "아이디",
                                            width: "40%",
                                            key: "a_id",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                        {
                                            title: "이름",
                                            width: "45%",
                                            key: "a_name",
                                            className: twMerge(theadStyle, "text-[#666666]"),
                                        },
                                    ]}
                                    tbody={data?.data_list ?? []}
                                    uniqueKeyName="idx"
                                    isLoading={isLoading}
                                    trClassName="hover:bg-[#f8f8f8] cursor-pointer"
                                    trClick={row => navigate(`${(row as AdminAccountListItem).idx}`, { state: { page: searchParams.get("page") ?? "1" } })}
                                />

                                <div className="flex justify-end mt-5">
                                    <button
                                        className="bg-black text-white rounded-[5px] px-4 py-2"
                                        onClick={() => navigate("create", { state: { page: searchParams.get("page") ?? "1" } })}
                                    >
                                        추가
                                    </button>
                                </div>
                            </div>

                            <Pagination
                                current={searchForm.page}
                                total={data?.total_count ?? 1}
                                pageSize={Number(searchForm.limit)}
                                onChange={newPage => {
                                    searchParams.set("page", String(newPage));
                                    setSearchParams(searchParams);
                                    setSearchForm(prev => ({ ...prev, page: newPage }));
                                }}
                            />
                        </div>
                    </div>
                </div>
            </div>
            <Toaster position="bottom-center" reverseOrder={false} />
        </>
    );
}
